import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import PropertyCard from './components/PropertyCard';
import EmptyState from './components/EmptyState';
import SearchAndSort from './components/SearchAndSort';
import SubscriptionBanner from './components/SubscriptionBanner';
import { useAuth } from '../../contexts/AuthContext';
import { propertyService } from '../../services/propertyService';
import { stripeService } from '../../services/stripeService';
import PilotExpirationModal from '../../components/PilotExpirationModal';

const AgentDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading, profileLoading } = useAuth();

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('dateAdded');
  const [filterActive, setFilterActive] = useState('active');
  const [showPilotModal, setShowPilotModal] = useState(false);

  const userTier = userProfile?.subscription_tier || 'free';
  const propertyLimit = userTier === 'free' ? 1 : Infinity;

  // Fetch properties on mount
  useEffect(() => {
    if (!authLoading && !profileLoading && user) {
      loadProperties();
      checkPilotExpiration();
    }
  }, [authLoading, profileLoading, user]);

  const checkPilotExpiration = () => {
    // Check if pilot is expiring soon and warning hasn't been shown yet
    if (stripeService?.isPilotExpiringSoon(userProfile) && !userProfile?.pilot_warning_shown) {
      setShowPilotModal(true);
    }
  };

  const handleDismissPilotModal = async () => {
    // Mark warning as shown so it doesn't appear again immediately
    await stripeService?.markPilotWarningShown(user?.id);
  };

  const loadProperties = async () => {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await propertyService?.getProperties(user?.id);

    if (fetchError) {
      setError(fetchError);
    } else {
      setProperties(data || []);
    }

    setLoading(false);
  };

  // Calculate metrics
  const metrics = useMemo(() => {
    const activeProperties = properties?.filter((p) => p?.isActive !== false)?.length;
    const totalOffers = properties?.reduce((sum, p) => sum + p?.offerCount, 0);
    const recentReports = properties?.filter((p) => {
      if (!p?.lastReportDate) return false;
      const daysDiff = Math.floor((new Date() - p?.lastReportDate) / (1000 * 60 * 60 * 24));
      return daysDiff <= 7;
    })?.length;

    return {
      totalProperties: properties?.length,
      activeProperties,
      totalOffers,
      recentReports
    };
  }, [properties]);

  // Filter and sort properties
  const filteredAndSortedProperties = useMemo(() => {
    let filtered = properties;

    // Apply active filter
    if (filterActive === 'active') {
      filtered = filtered?.filter((p) => p?.isActive !== false);
    } else if (filterActive === 'inactive') {
      filtered = filtered?.filter((p) => p?.isActive === false);
    }

    // Apply search filter
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter((p) =>
      p?.address?.toLowerCase()?.includes(query) ||
      p?.owner?.toLowerCase()?.includes(query)
      );
    }

    // Apply sorting
    const sorted = [...filtered]?.sort((a, b) => {
      switch (sortBy) {
        case 'dateAdded':
          return b?.dateAdded - a?.dateAdded;
        case 'dateAddedOld':
          return a?.dateAdded - b?.dateAdded;
        case 'address':
          return a?.address?.localeCompare(b?.address);
        case 'addressDesc':
          return b?.address?.localeCompare(a?.address);
        case 'offerCount':
          return b?.offerCount - a?.offerCount;
        case 'offerCountLow':
          return a?.offerCount - b?.offerCount;
        default:
          return 0;
      }
    });

    return sorted;
  }, [properties, searchQuery, sortBy, filterActive]);

  const handleAddProperty = () => {
    if (userTier === 'free' && properties?.length >= propertyLimit) {
      navigate('/account?tab=subscription');
    } else {
      navigate('/property-creation');
    }
  };

  const handleViewOffers = (propertyId) => {
    navigate(`/offer-management?propertyId=${propertyId}`);
  };

  const handleGenerateReport = (propertyId) => {
    navigate(`/report-generation?propertyId=${propertyId}`);
  };

  const handleEditProperty = (propertyId) => {
    navigate(`/property-creation?edit=${propertyId}`);
  };

  const handleToggleActive = async (propertyId, isActive) => {
    const { error: toggleError } = await propertyService?.togglePropertyActive(propertyId, isActive);

    if (toggleError) {
      console.error('Failed to toggle property active state:', toggleError);
      return;
    }

    // Update local state
    setProperties((prev) =>
      prev?.map((p) => (p?.id === propertyId ? { ...p, isActive } : p))
    );
  };

  const breadcrumbItems = [
  { label: 'Dashboard', path: '/agent-dashboard', icon: 'LayoutDashboard' }];


  const canAddProperty = userTier === 'pro' || properties?.length < propertyLimit;

  // Show loading only if auth/profile is loading OR properties are being fetched for the first time
  if (authLoading || (profileLoading && loading)) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading properties...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-md">
            <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Failed to Load Properties</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={loadProperties}>Try Again</Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Agent Dashboard - OfferSense</title>
        <meta name="description" content="Manage your real estate properties, track offers, and generate professional comparison reports for your clients." />
      </Helmet>
      <Header />
      <Breadcrumbs items={breadcrumbItems} />
      <div className="min-h-screen bg-background">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-6 md:py-8 lg:py-10">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                My Properties
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manage your listings, track offers, and generate reports
              </p>
            </div>
            <Button
              variant="default"
              size="lg"
              iconName="Plus"
              iconPosition="left"
              onClick={handleAddProperty}
              disabled={!canAddProperty}>

              Add Property
            </Button>
          </div>

          {/* Subscription Banner */}
          <SubscriptionBanner
            tier={userTier}
            propertyCount={properties?.length}
            propertyLimit={propertyLimit} />

          {showPilotModal && (
            <PilotExpirationModal
              userProfile={userProfile}
              onClose={() => setShowPilotModal(false)}
              onDismiss={handleDismissPilotModal}
            />
          )}

          {/* Search and Sort */}
          {properties?.length > 0 &&
          <SearchAndSort
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sortBy={sortBy}
            onSortChange={setSortBy}
            filterActive={filterActive}
            onFilterChange={setFilterActive} />

          }

          {/* Properties Grid or Empty State */}
          {filteredAndSortedProperties?.length > 0 ?
          <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredAndSortedProperties?.map((property) =>
                <PropertyCard
                    key={property?.id}
                    property={property}
                    onViewOffers={handleViewOffers}
                    onGenerateReport={handleGenerateReport}
                    onEdit={handleEditProperty}
                    onToggleActive={handleToggleActive} />

                )}
              </div>

              {/* Results Info */}
              <div className="mt-6 md:mt-8 text-center">
                <p className="text-sm md:text-base text-muted-foreground">
                  Showing {filteredAndSortedProperties?.length} of {properties?.length} {properties?.length === 1 ? 'property' : 'properties'}
                </p>
              </div>
            </> :
          properties?.length === 0 ?
          <EmptyState onAddProperty={handleAddProperty} /> :

          <div className="text-center py-12 md:py-16">
              <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4 md:w-16 md:h-16" />
              <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                No properties found
              </h3>
              <p className="text-sm md:text-base text-muted-foreground mb-6">
                Try adjusting your search or filters
              </p>
              <Button
              variant="outline"
              size="default"
              iconName="X"
              iconPosition="left"
              onClick={() => setSearchQuery('')}>

                Clear Search
              </Button>
            </div>
          }
        </div>
      </div>
    </>);

};

export default AgentDashboard;